import type { PropsWithChildren } from 'react';

import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { LicenseInfo } from '@mui/x-license';

import { LocaleProvider } from './locale/locale-provider';
import { useMuiLocale } from './locale/mui-locale-provider/mui-locale-provider';
import {
  type FenrirThemeProviderProps,
  FenrirThemeProvider,
} from './theme/fenrir-theme-provider';
import type { FenrirThemeMode } from './theme-mode/theme-mode';
import { useDefaultThemeMode } from './theme-mode/use-default-theme-mode/use-default-theme-mode';

const DEFAULT_MUI_KEY =
  '8d5abf43b4f707dd92e51d6f78adc54bTz0xMDQzODIsRT0xNzY1OTg3MzQ1MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=';

interface FenrirProviderProps {
  /**
   * The default mode when the local storage has no mode yet,
   * can be 'light' | 'dark' | 'system'
   * if 'system' is passed it will default to the user's system preference and save 'dark' or 'light' in localStorage.
   * @default 'light'
   */
  initialMode?: FenrirThemeMode;
  licenseKey?: string;
}

type MuiProvidersProps = Pick<FenrirThemeProviderProps, 'defaultMode'>;

const MuiProviders = ({
  children,
  defaultMode,
}: PropsWithChildren<MuiProvidersProps>) => {
  const { dayJsLocale, localization } = useMuiLocale();

  return (
    <FenrirThemeProvider defaultMode={defaultMode} localization={localization}>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={dayJsLocale}
      >
        {children}
      </LocalizationProvider>
    </FenrirThemeProvider>
  );
};

export const FenrirProvider = ({
  // TODO: Remove to allow default mode to be system
  initialMode = 'light',
  licenseKey = DEFAULT_MUI_KEY,
  children,
}: PropsWithChildren<FenrirProviderProps>) => {
  const defaultMode = useDefaultThemeMode(initialMode);

  if (licenseKey) {
    LicenseInfo.setLicenseKey(licenseKey);
  }

  return (
    <LocaleProvider>
      <MuiProviders defaultMode={defaultMode}>{children}</MuiProviders>
    </LocaleProvider>
  );
};
